import { api } from './api';

/**
 * Benachrichtigungen aufs Geraet (7.40), die Seite dazu.
 *
 * Drei Dinge muessen zusammenkommen, und jedes kann fehlen: der Browser muss
 * Push koennen, die Person muss zustimmen, und der Server muss die Anmeldung
 * behalten. Deshalb sagt `stand()` nicht "an/aus", sondern welcher der drei
 * Schritte offen ist -- ein Schalter, der nur umkippt, laesst jemanden im
 * Dunkeln, wenn iOS die Frage gar nicht erst stellt.
 */
export type PushStand =
  | 'geht-nicht'
  | 'nicht-vom-home-bildschirm'
  | 'noch-nicht'
  | 'abgelehnt'
  | 'aus'
  | 'an';

/** Ob die App vom Home-Bildschirm laeuft. Auf dem iPhone die harte Bedingung. */
export function vomHomeBildschirm(): boolean {
  const alsApp =
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as { standalone?: boolean }).standalone === true;
  return alsApp;
}

function amIPhone(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !('MSStream' in window);
}

export async function stand(): Promise<PushStand> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
    // Auf dem iPhone fehlt PushManager genau dann, wenn die Seite im Browser
    // laeuft statt vom Home-Bildschirm -- das ist keine kaputte App, sondern
    // eine Bedingung, und der Unterschied gehoert auf den Schirm.
    return amIPhone() && !vomHomeBildschirm() ? 'nicht-vom-home-bildschirm' : 'geht-nicht';
  }
  if (Notification.permission === 'denied') return 'abgelehnt';
  const reg = await bereit();
  // Kein Worker, kein Push. `navigator.serviceWorker.ready` wartet darauf
  // ewig -- im Entwicklungsserver ist keiner angemeldet, und auf einem
  // frisch geladenen Telefon fuer einen Augenblick auch nicht. Ohne die
  // Frist stuende hier fuer immer „Einen Moment…“. Und es ist nicht dasselbe
  // wie „geht nicht“: der Browser kann es, der Worker ist nur noch nicht da.
  if (!reg) return 'noch-nicht';
  const vorhanden = await reg.pushManager.getSubscription();
  return vorhanden ? 'an' : 'aus';
}

/** Der Service Worker, sobald er da ist -- oder nach drei Sekunden nichts. */
async function bereit(frist = 3000): Promise<ServiceWorkerRegistration | null> {
  const sofort = await navigator.serviceWorker.getRegistration();
  if (sofort?.active) return sofort;
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise<null>((loese) => setTimeout(() => loese(null), frist)),
  ]);
}

/** base64url aus dem Server in das Uint8Array, das der Browser will. */
function schluesselBytes(base64: string): ArrayBuffer {
  const gefuellt = base64.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (base64.length % 4)) % 4);
  const roh = atob(gefuellt);
  // Ein ArrayBuffer, kein Uint8Array: `applicationServerKey` will einen
  // BufferSource, und ein Uint8Array kann laut Typ auf einem SharedArrayBuffer
  // sitzen -- den nimmt die Schnittstelle nicht.
  const puffer = new ArrayBuffer(roh.length);
  const bytes = new Uint8Array(puffer);
  for (let i = 0; i < roh.length; i++) bytes[i] = roh.charCodeAt(i);
  return puffer;
}

/**
 * Fragt und meldet an. Muss aus einer Beruehrung heraus laufen: `requestPermission`
 * ohne Geste beantwortet Safari mit einem Nein, das bleibt.
 */
export async function einschalten(): Promise<PushStand> {
  const erlaubnis = await Notification.requestPermission();
  if (erlaubnis !== 'granted') return erlaubnis === 'denied' ? 'abgelehnt' : 'aus';
  const reg = await bereit();
  if (!reg) return 'noch-nicht';
  const { publicKey } = await api.pushSchluessel();
  const anmeldung =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({
      // Ohne das lehnt jeder Browser ab: eine Meldung, die niemand sieht, darf
      // es nicht geben.
      userVisibleOnly: true,
      applicationServerKey: schluesselBytes(publicKey),
    }));
  const roh = anmeldung.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
  if (!roh.endpoint || !roh.keys?.p256dh || !roh.keys?.auth) return 'aus';
  await api.pushAnmelden({
    endpoint: roh.endpoint,
    keys: { p256dh: roh.keys.p256dh, auth: roh.keys.auth },
    geraet: navigator.userAgent.slice(0, 200),
  });
  return 'an';
}

export async function ausschalten(): Promise<PushStand> {
  const reg = await bereit();
  const anmeldung = await reg?.pushManager.getSubscription();
  if (!anmeldung) return 'aus';
  // Erst dem Server sagen, dann beim Browser abmelden: andersherum kennt
  // niemand mehr die Adresse, die aus der Tabelle sollte.
  await api.pushAbmelden(anmeldung.endpoint).catch(() => {});
  await anmeldung.unsubscribe();
  return 'aus';
}
